import {
  Autocomplete,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField
} from '@mui/material';
import { EntitlementGroupInterface, RelationType } from '@thro-types/access-handler';

import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useEntitlementsQuery } from 'api/hooks/accessHandler';
import { CourseSelect } from 'app/components/CourseSelect';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Faculty } from 'types/AppTypes';

interface Props {
  faculties: Faculty[];
}

export function EntitlementGroupFormGeneral({ faculties }: Props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { values, errors, setFieldValue, touched, setFieldTouched, validateForm } =
    useFormikContext<EntitlementGroupInterface>();

  const { data: entitlementsData, isError, isLoading } = useEntitlementsQuery();

  if (isError) {
    dispatch(
      AlertActions.error(t(translations.pages.entitlementGroups.alerts.errorLoadingEntitlements()))
    );
    navigate('/', { replace: true });
    return;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Field
          as={TextField}
          name="name"
          required
          error={touched.name && errors.name}
          helperText={touched.name && errors.name}
          fullWidth
          label={t(translations.common.name())}
          size="small"
        />
      </Grid>
      <Grid item xs={6}>
        <FormControl
          required
          fullWidth
          error={touched.relationType && Boolean(errors.relationType)}
          size="small"
        >
          <InputLabel>{t(translations.common.type())}</InputLabel>
          <Select
            label={t(translations.common.type())}
            fullWidth
            value={values.relationType}
            onChange={async e => {
              await setFieldValue('faculty', null);
              await setFieldValue('course', null);
              await setFieldValue('relationType', e.target.value);
              await validateForm();
            }}
          >
            {Object.keys(RelationType).map(key => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </Select>
          {touched.relationType && errors.relationType && (
            <FormHelperText>{errors.relationType}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl
          required
          fullWidth
          disabled={values.relationType !== 'FACULTY'}
          error={touched.faculty && Boolean(errors.faculty)}
          size="small"
        >
          <InputLabel>{t(translations.common.faculty())}</InputLabel>
          <Select
            label={t(translations.common.faculty())}
            fullWidth
            value={values.faculty || ''}
            onChange={e => setFieldValue('faculty', e.target.value)}
          >
            <MenuItem key={0} value="">
              {'---'}
            </MenuItem>
            {faculties.map(faculty => {
              return (
                <MenuItem key={faculty.id} value={faculty.id}>
                  {faculty.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl
          required
          fullWidth
          error={touched.course && Boolean(errors.course)}
          size="small"
        >
          <CourseSelect
            course={values.course || ''}
            setCourse={course => setFieldValue('course', course)}
            disabled={values.relationType !== 'COURSE'}
            shouldUseIdAsValue
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            multiple
            loading={isLoading}
            options={entitlementsData?.data || []}
            getOptionDisabled={option => option.entitlementGroupId !== null}
            value={
              values.entitlements &&
              values.entitlements.map(selectedEntitlement => {
                return entitlementsData?.data.find(
                  ent => ent.id === (selectedEntitlement as unknown as number)
                )!;
              })
            }
            getOptionLabel={ent => ent.name}
            onChange={(ent, value, reason) => {
              setFieldValue(
                'entitlements',
                value.map(ent => ent.id)
              );
            }}
            renderInput={params => (
              <TextField
                {...params}
                error={touched.entitlements && Boolean(errors.entitlements)}
                helperText={touched.entitlements && errors.entitlements}
                variant="outlined"
                label={t(translations.pages.entitlementGroups.form.entitlements())}
                fullWidth
                onBlur={() => {
                  if (!touched.entitlements) {
                    setFieldTouched('entitlements', true);
                  }
                }}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password'
                }}
                size="small"
              />
            )}
            renderTags={(value, getPropTags) => {
              if (isLoading) return;
              return value.map((ent, index) => (
                <Chip
                  component={Paper}
                  elevation={2}
                  {...getPropTags({ index: index })}
                  key={ent.id}
                  label={ent.name}
                />
              ));
            }}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
}
